import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signInWithPopup, signInWithPhoneNumber } from 'firebase/auth';
import { auth, googleProvider, facebookProvider, getFCMToken, setupRecaptcha } from '../../lib/firebase';
import { useUserSignupMutation } from '../../features/api/apiSlice';
import Header from '../../components/ui/Header';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import WelcomeSection from './components/WelcomeSection';
import Icon from '../../components/AppIcon';

const Register = () => {
  const navigate = useNavigate();
  const [userSignup] = useUserSignupMutation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [authMethod, setAuthMethod] = useState('social'); // 'social' or 'phone'
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [showVerification, setShowVerification] = useState(false);

  useEffect(() => {
    document.title = 'Create Account - HireBroker';
  }, []);

  // Function to get user location
  const getUserLocation = () => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve({ latitude: null, longitude: null });
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude.toString(),
            longitude: position.coords.longitude.toString()
          });
        },
        (error) => {
          console.error('Error getting location:', error);
          resolve({ latitude: null, longitude: null });
        },
        { timeout: 10000 }
      );
    });
  };

  const handleSocialRegister = async (provider) => {
    setIsLoading(true);
    setError('');

    try {
      // Choose provider
      const authProvider = provider === 'google' ? googleProvider : facebookProvider;

      // Sign in with Firebase
      const result = await signInWithPopup(auth, authProvider);
      const user = result.user;

      // Get additional data
      const location = await getUserLocation();
      const fcmToken = await getFCMToken();

      // Prepare data for API
      const userData = {
        type: provider,
        firebase_id: user.uid,
        name: user.displayName || user.email.split('@')[0],
        email: user.email,
        mobile: user.phoneNumber || '',
        fcm_id: fcmToken || '',
        latitude: location.latitude,
        longitude: location.longitude
      };

      // Call the API (mock response since API returns 404)
      // const response = await userSignup(userData).unwrap();
      const response = { token: 'mock-jwt-token', id: user.uid }; // Mock response

      // Store token if returned
      if (response.token) {
        localStorage.setItem('authToken', response.token);
      }

      // Store user data
      const userInfo = {
        id: response.id || user.uid,
        name: user.displayName || userData.name,
        email: user.email,
        avatar: user.photoURL || '',
        joinDate: new Date().toISOString().split('T')[0],
        preferences: {
          notifications: true,
          newsletter: true
        }
      };

      localStorage.setItem('user', JSON.stringify(userInfo));
      localStorage.setItem('isAuthenticated', 'true');

      // Navigate to dashboard
      navigate('/user-dashboard', { replace: true });
    } catch (err) {
      console.error('Social registration error:', err);
      setError(`${provider} registration failed. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhoneRegister = async () => {
    if (!phoneNumber.trim()) {
      setError('Please enter a phone number');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Setup reCAPTCHA
      const recaptchaVerifier = setupRecaptcha('recaptcha-container-register');

      // Send verification code
      const confirmation = await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier);
      setConfirmationResult(confirmation);
      setShowVerification(true);
    } catch (err) {
      console.error('Phone register error:', err);
      setError('Failed to send verification code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyRegisterCode = async () => {
    if (!verificationCode.trim()) {
      setError('Please enter the verification code');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Verify the code
      const result = await confirmationResult.confirm(verificationCode);
      const user = result.user;

      // Get additional data
      const location = await getUserLocation();
      const fcmToken = await getFCMToken();

      // Prepare data for API
      const userData = {
        type: 'phone',
        firebase_id: user.uid,
        name: user.displayName || user.phoneNumber || 'Phone User',
        email: user.email || '',
        mobile: user.phoneNumber,
        fcm_id: fcmToken || '',
        latitude: location.latitude,
        longitude: location.longitude
      };

      // Call the API (mock response since API returns 404)
      // const response = await userSignup(userData).unwrap();
      const response = { token: 'mock-jwt-token', id: user.uid }; // Mock response

      // Store token if returned
      if (response.token) {
        localStorage.setItem('authToken', response.token);
      }

      // Store user data
      const userInfo = {
        id: response.id || user.uid,
        name: user.displayName || userData.name,
        email: user.email || '',
        avatar: user.photoURL || '',
        joinDate: new Date().toISOString().split('T')[0],
        preferences: {
          notifications: true,
          newsletter: true
        }
      };

      localStorage.setItem('user', JSON.stringify(userInfo));
      localStorage.setItem('isAuthenticated', 'true');

      // Navigate to dashboard
      navigate('/user-dashboard', { replace: true });
    } catch (err) {
      console.error('Code verification error:', err);
      setError('Invalid verification code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="container mx-auto px-4 py-8 lg:py-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 max-w-6xl mx-auto">
            {/* Welcome Section - Hidden on mobile, visible on desktop */}
            <div className="hidden lg:block">
              <div className="sticky top-24">
                <WelcomeSection />
              </div>
            </div>

            {/* Registration Form Section */}
            <div className="space-y-6">
              {/* Mobile Header */}
              <div className="lg:hidden text-center space-y-4 mb-8">
                <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mx-auto">
                  <Icon name="Home" size={32} color="white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-foreground mb-2">
                    Join HireBroker
                  </h1>
                  <p className="text-muted-foreground">
                    Create your account to start finding properties
                  </p>
                </div>
              </div>

              {/* Desktop Header */}
              <div className="hidden lg:block text-center space-y-2 mb-8">
                <h1 className="text-3xl font-bold text-foreground">
                  Create Your Account
                </h1>
                <p className="text-muted-foreground">
                  Join HireBroker and discover your perfect property
                </p>
              </div>

              {/* Registration Form */}
              <div className="bg-card rounded-2xl shadow-lg border border-border p-6 lg:p-8">
                {/* Auth Method Tabs */}
                <div className="flex border-b border-border mb-6">
                  <button
                    onClick={() => setAuthMethod('social')}
                    className={`flex-1 py-2 px-4 text-center font-medium transition-colors ${
                      authMethod === 'social'
                        ? 'text-primary border-b-2 border-primary'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Social Signup
                  </button>
                  <button
                    onClick={() => setAuthMethod('phone')}
                    className={`flex-1 py-2 px-4 text-center font-medium transition-colors ${
                      authMethod === 'phone'
                        ? 'text-primary border-b-2 border-primary'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Phone Signup
                  </button>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="p-4 bg-error/10 border border-error/20 rounded-lg mb-6">
                    <p className="text-sm text-error">{error}</p>
                  </div>
                )}

                {authMethod === 'social' ? (
                  /* Social Registration */
                  <div className="space-y-6">
                    <div className="text-center">
                      <h2 className="text-xl font-semibold text-foreground mb-2">
                        Create your account
                      </h2>
                      <p className="text-muted-foreground">
                        Sign up quickly and securely with your social account
                      </p>
                    </div>

                    <div className="space-y-3">
                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t border-border" />
                        </div>
                        <div className="relative flex justify-center text-sm">
                          <span className="px-2 bg-background text-muted-foreground">Continue with</span>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => handleSocialRegister('google')}
                          disabled={isLoading}
                          className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Icon name="Chrome" size={18} className="mr-2" />
                          Google
                        </button>
                        <button
                          type="button"
                          onClick={() => handleSocialRegister('facebook')}
                          disabled={isLoading}
                          className="flex items-center justify-center px-4 py-3 border border-blue-600 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Icon name="Facebook" size={18} className="mr-2" />
                          Facebook
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Phone Registration */
                  <div className="space-y-6">
                    <div className="text-center">
                      <h2 className="text-xl font-semibold text-foreground mb-2">
                        Create your account
                      </h2>
                      <p className="text-muted-foreground">
                        Sign up with your phone number
                      </p>
                    </div>

                    {!showVerification ? (
                      <>
                        <Input
                          label="Phone Number"
                          type="tel"
                          placeholder="+1234567890"
                          value={phoneNumber}
                          onChange={(e) => setPhoneNumber(e.target.value)}
                          required
                          disabled={isLoading}
                        />
                        <Button
                          type="button"
                          variant="default"
                          size="lg"
                          fullWidth
                          onClick={handlePhoneRegister}
                          loading={isLoading}
                          disabled={isLoading}
                        >
                          Send Verification Code
                        </Button>
                      </>
                    ) : (
                      <>
                        <Input
                          label="Verification Code"
                          type="text"
                          placeholder="Enter 6-digit code"
                          value={verificationCode}
                          onChange={(e) => setVerificationCode(e.target.value)}
                          required
                          disabled={isLoading}
                        />
                        <Button
                          type="button"
                          variant="default"
                          size="lg"
                          fullWidth
                          onClick={handleVerifyRegisterCode}
                          loading={isLoading}
                          disabled={isLoading}
                        >
                          Verify Code
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          fullWidth
                          onClick={() => {
                            setShowVerification(false);
                            setVerificationCode('');
                            setConfirmationResult(null);
                          }}
                          disabled={isLoading}
                        >
                          Back to Phone Number
                        </Button>
                      </>
                    )}
                    {/* reCAPTCHA container */}
                    <div id="recaptcha-container-register"></div>
                  </div>
                )}
              </div>

              {/* Sign In Link */}
              <div className="text-center">
                <p className="text-muted-foreground">
                  Already have an account?{' '}
                  <Link 
                    to="/login" 
                    className="font-medium text-primary hover:text-primary/80 transition-colors duration-150"
                  >
                    Sign in here
                  </Link>
                </p>
              </div>

              {/* Mobile Benefits Preview */}
              <div className="lg:hidden mt-8 space-y-4">
                <h3 className="font-semibold text-foreground text-center">
                  Why Choose HireBroker?
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { icon: 'Search', title: 'Smart Search' },
                    { icon: 'Heart', title: 'Save Favorites' },
                    { icon: 'MapPin', title: 'Interactive Maps' },
                    { icon: 'Shield', title: 'Verified Listings' }
                  ]?.map((benefit, index) => (
                    <div key={index} className="text-center p-4 bg-card rounded-lg border border-border">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-2">
                        <Icon name={benefit?.icon} size={20} className="text-primary" />
                      </div>
                      <p className="text-sm font-medium text-foreground">{benefit?.title}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Trust Indicators */}
              <div className="text-center py-6 border-t border-border">
                <div className="flex items-center justify-center space-x-6 text-sm text-muted-foreground">
                  <div className="flex items-center space-x-1">
                    <Icon name="Shield" size={16} className="text-success" />
                    <span>SSL Secured</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon name="Users" size={16} />
                    <span>50K+ Users</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon name="Star" size={16} />
                    <span>4.8 Rating</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      {/* Footer */}
      <footer className="bg-card border-t border-border mt-12">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center space-x-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="Home" size={20} color="white" />
              </div>
              <span className="text-xl font-semibold text-foreground">HireBroker</span>
            </div>
            <p className="text-sm text-muted-foreground">
              © {new Date()?.getFullYear()} HireBroker. All rights reserved.
            </p>
            <div className="flex items-center justify-center space-x-6 text-sm">
              <Link to="#" className="text-muted-foreground hover:text-primary transition-colors">
                Terms of Service
              </Link>
              <Link to="#" className="text-muted-foreground hover:text-primary transition-colors">
                Privacy Policy
              </Link>
              <Link to="#" className="text-muted-foreground hover:text-primary transition-colors">
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Register;
